/**
 * EAA-1764 (D16/B13): organization & tenant admin API client.
 *
 * Talks to portal-api's org-management surface (`/api/v1/me/admin`,
 * `/api/v1/organizations…`). Same conventions as `meAccess.ts`: plain fetch,
 * standard envelope `{success, code, data}` unwrap, a typed 401 error so
 * callers re-run `login()` instead of rendering a failure. Authorization is
 * ALWAYS re-checked server-side — these types only shape what the UI shows.
 */

import { useSessionStore } from '../store/sessionStore';

export interface MeAdminOrg {
    id: string;
    code: string;
    name: string;
    // EAA-1846: null for a root org — lets the acting-org fallback prefer a
    // root instead of picking organizations[0] from this alphabetical-by-name
    // list, which let a newly created org whose name sorted before the real
    // root silently become every fresh session's default console scope.
    parent_id: string | null;
}

/** EAA-1796 (P4a): the resolved acting org echoed by `GET /me/acting-org`. */
export interface ActingOrg {
    id: string;
    code: string;
    name: string;
}

export interface MeAdminData {
    is_super_admin: boolean;
    organizations: MeAdminOrg[];
    /**
     * EAA-1776 (§1b): the SERVER-computed console capability set — the FE's only
     * permission source (D10: never derived from claims or is_super_admin on the
     * client). Optional so a portal-api that predates EAA-1775 parses fine; the
     * consumer treats absence as [] (fail-closed).
     */
    capabilities?: string[];
}

export interface OrgBranding {
    display_name?: string;
    logo_url?: string;
    primary_color?: string;
    primary_color_hover?: string;
}

export interface Organization {
    id: string;
    code: string;
    name: string;
    /**
     * EAA-1818: the parent org's UUID, or `null` for a root org — the self-FK
     * adjacency link `OrganizationSerializer` started exposing in EAA-1836.
     * This is what makes `/console/hierarchy`'s real nested tree possible; it
     * was absent from every serializer before that ticket.
     */
    parent: string | null;
    /**
     * EAA-1838: the assigned `OrgUnitType`'s UUID — WHAT KIND of đơn vị this is.
     *
     * Replaced the old `tier` field. `null` is a defensive shape only:
     * `OrganizationService.create` always resolves a type (seeding the root's
     * permissive default when the caller names none), precisely so the reparent
     * rule cannot go inert the way the nullable `tier` made it.
     */
    unit_type: string | null;
    is_active: boolean;
    branding: OrgBranding;
    created_at: string;
    updated_at: string;
}

/**
 * EAA-1838: one KIND of đơn vị in a ROOT organization's own vocabulary
 * ("Bộ", "Sở", "Trường", "Phòng chuyên môn"). There is no fixed, system-wide
 * list: names, count and meaning are per-root.
 *
 * REPLACED `OrgTier`. Order is no longer a `rank` integer — it is expressed
 * directly by `allowed_children`, the set of types that may sit immediately
 * below this one. That one field subsumes both `rank` and the old
 * `allow_self_nest` flag (a type that may nest under itself simply lists its own
 * id), and it can express things a ladder could not: a "Sở" holding BOTH a
 * "Trường" and a "Phòng chuyên môn", or a deliberate rung-skip.
 *
 * `can_be_root` answers the one containment question no parent can: whether a
 * đơn vị of this kind may sit at the top of the tree.
 *
 * `is_active: false` is a HIDE, never a delete: portal-api has no DELETE for this
 * model, and `Organization.unit_type` is PROTECT so a type in use cannot vanish.
 * A hidden type must stay visible (greyed) wherever it is managed, or an admin
 * loses the only way to bring it back.
 */
export interface OrgUnitType {
    id: string;
    /** The root organization owning this vocabulary. */
    organization: string;
    name: string;
    /** Stable identifier, unique per organization — immutable after creation. */
    code: string;
    /** Type ids that may sit directly under this one; includes self when self-nesting. */
    allowed_children: string[];
    can_be_root: boolean;
    is_active: boolean;
}

/** EAA-1818: `DELETE /organizations/{id}` — a soft deactivate plus its blast radius. */
export interface OrganizationDeactivated {
    organization: Organization;
    accounts_affected: number;
}

export interface OrgAdminRow {
    id: string;
    user_sub: string;
    user_email: string;
    is_active: boolean;
    granted_by: string;
    granted_at: string;
}

/** One-time issuance result — `temp_password` appears exactly once, never again. */
export interface OrgAdminIssued {
    admin: OrgAdminRow;
    temp_password: string | null;
    username: string;
}

export interface OrgTenant {
    id: string;
    tenant_id: string;
    name: string | null;
    slug: string | null;
    tenant_is_active: boolean;
    is_active: boolean;
    created_at: string;
}

/** Fresh ACH provision returns the default API token exactly once (D12). */
export interface OrgTenantCreated {
    tenant: OrgTenant;
    api_token: { key: string } | null;
}

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    data: T;
    /**
     * EAA-1818: per-field validation details. This is a TOP-LEVEL envelope key,
     * not a member of `data` — portal-api's `error_response()` sets `data: None`
     * and attaches `details` beside it, so reading `data` on a failure (which is
     * what this client used to do) always yielded `null`. Each value is a list
     * of `{code, message}` after `normalize_manual_error_details` rewrites the
     * bare strings a view passes.
     */
    details?: Record<string, unknown>;
}

/** One normalized entry of `ApiEnvelope.details[field]`. */
export interface ApiValidationDetailItem {
    code: string;
    message: string;
}

/**
 * EAA-1818: pull the first server message for one field out of a thrown
 * `OrgAdminApiError`. Used to map the organization-move rules — portal-api
 * returns those as English strings under `details.parent`, unlocalized by
 * design, so the caller has to translate them (see `dragRules.ts`).
 */
export function fieldDetailMessage(details: unknown, field: string): string | null {
    if (!details || typeof details !== 'object') return null;
    const entries = (details as Record<string, unknown>)[field];
    if (!Array.isArray(entries) || entries.length === 0) return null;
    const first = entries[0];
    if (typeof first === 'string') return first;
    if (
        first &&
        typeof first === 'object' &&
        typeof (first as ApiValidationDetailItem).message === 'string'
    ) {
        return (first as ApiValidationDetailItem).message;
    }
    return null;
}

// EAA-1839: exported (was module-private) so the scoped-accounts screen can type
// its own page-walk over `fetchScopedAccounts` without re-declaring the envelope.
export interface Paginated<T> {
    results: T[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export class OrgAdminUnauthorizedError extends Error {
    constructor() {
        super('Unauthorized');
        this.name = 'OrgAdminUnauthorizedError';
    }
}

export class OrgAdminApiError extends Error {
    readonly code: number;
    readonly details?: unknown;
    constructor(message: string, code: number, details?: unknown) {
        super(message);
        this.name = 'OrgAdminApiError';
        this.code = code;
        this.details = details;
    }
}

async function request<T>(
    apiBase: string,
    token: string,
    path: string,
    init?: { method?: string; body?: unknown; orgId?: string }
): Promise<T> {
    // EAA-1797 (P4b): the console's acting organization rides on every request as
    // X-Org-Id. An explicit `init.orgId` wins (used to validate a candidate org
    // before committing it); otherwise the current per-tab selection is sent. The
    // server re-authorizes it (P4a, EAA-1796) — this is context, never a grant.
    const orgId = init?.orgId ?? useSessionStore.getState().actingOrgId;
    const res = await fetch(`${apiBase}${path}`, {
        method: init?.method ?? 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            ...(orgId ? { 'X-Org-Id': orgId } : {}),
            ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        // EAA-1818: carry `envelope.details` (top-level, beside a null `data`)
        // rather than `envelope.data` — the latter is always null on a failure,
        // so field-level validation errors were being silently dropped here.
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            envelope?.details
        );
    }
    return envelope.data;
}

export function fetchMeAdmin(apiBase: string, token: string): Promise<MeAdminData> {
    return request<MeAdminData>(apiBase, token, '/api/v1/me/admin');
}

/**
 * EAA-1797 (P4b): validate a candidate acting org against the P4a seam. Sends the
 * candidate as X-Org-Id explicitly (not the stored one) so the picker can confirm
 * a selection before committing it. Rejects (404) when the caller may not act as
 * it — the provider then falls back to a valid org.
 */
export function fetchActingOrg(apiBase: string, token: string, orgId: string): Promise<ActingOrg> {
    return request<ActingOrg>(apiBase, token, '/api/v1/me/acting-org', { orgId });
}

// EAA-1807: widened additively (all params optional) so the console
// organizations screen can drive real server-side search/ordering/pagination
// — GET /organizations already supported these, the client just never sent
// them (page_size=100 + client-side "show everything, no sort"). The one
// other caller (DirectoryPage) keeps calling with no params and only reads
// `.count`, which is unaffected by the page_size default changing.
// EAA-1818: `parentId` and `pageSize` added additively for the hierarchy tree.
// `parentId` is deliberately three-state and the distinction is load-bearing:
// `undefined` omits `parent_id` entirely (no parent filter — the pre-existing
// behaviour every other caller relies on), `null` requests ROOT orgs only, and
// a uuid requests that org's direct children. portal-api accepts both "" and
// "null" for the root case; this client always sends the literal "null" so one
// canonical form appears in logs and query keys.
export function fetchOrganizations(
    apiBase: string,
    token: string,
    params?: {
        page?: number;
        pageSize?: number;
        search?: string;
        ordering?: string;
        parentId?: string | null;
    }
): Promise<Paginated<Organization>> {
    const query = new URLSearchParams({ page_size: String(params?.pageSize ?? 20) });
    if (params?.page && params.page > 1) query.set('page', String(params.page));
    if (params?.search) query.set('search', params.search);
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.parentId !== undefined) query.set('parent_id', params.parentId ?? 'null');
    return request<Paginated<Organization>>(
        apiBase,
        token,
        `/api/v1/organizations?${query.toString()}`
    );
}

// EAA-1818: no `parent` here on purpose — `OrganizationCreateSerializer`
// (portal-api `directory/org_serializers.py`) accepts only {code, name,
// branding}, so a child org is created at root and then PATCHed into place.
// See `useHierarchyPage`'s createChildOrg for that two-call flow and why it is
// not hidden behind a fake single-call helper.
export function createOrganization(
    apiBase: string,
    token: string,
    // EAA-1838: `unit_type` added. Optional in the API because the very FIRST
    // organization has no root whose vocabulary it could draw from — the server
    // seeds a default type in that case. Every caller that already knows the
    // root (i.e. creating a child đơn vị) should send one.
    body: { code: string; name: string; branding?: OrgBranding; unit_type?: string }
): Promise<Organization> {
    return request<Organization>(apiBase, token, '/api/v1/organizations', { method: 'POST', body });
}

export function fetchOrganization(
    apiBase: string,
    token: string,
    orgId: string
): Promise<Organization> {
    return request<Organization>(apiBase, token, `/api/v1/organizations/${orgId}`);
}

// EAA-1818: `parent` added — the hierarchy screen's move. A uuid re-parents,
// `null` promotes the org to root. Super-admin only server-side (same D-09 gate
// as `is_active`); org-admins get AUTH_PERMISSION_DENIED, and a rule violation
// comes back as VALIDATION_ERROR with `details.parent`.
// EAA-1838: `unit_type` added — retype this đơn vị. NOTE the asymmetry, verified
// in portal-api `OrganizationDetailView.patch`: the super-admin gate there covers
// `is_active` and `parent` ONLY, so an org-admin may retype an org they
// administer. Authoring the vocabulary itself (POST/PATCH .../unit-types below)
// is super-admin only.
//
// NOT nullable, unlike the `tier` field it replaced: clearing a type would put
// the row back into the state where the reparent rule skips itself. The server
// also re-grades containment against the CURRENT parent, so a retype that would
// make the đơn vị illegal where it stands is rejected rather than left to
// surface later. Failures come back as VALIDATION_ERROR under `details.parent` —
// the same key every other rule failure uses, since that handler is shared
// (see `translateParentRuleMessage`).
export function updateOrganization(
    apiBase: string,
    token: string,
    orgId: string,
    body: {
        name?: string;
        branding?: OrgBranding;
        is_active?: boolean;
        parent?: string | null;
        unit_type?: string;
    }
): Promise<Organization> {
    return request<Organization>(apiBase, token, `/api/v1/organizations/${orgId}`, {
        method: 'PATCH',
        body,
    });
}

/**
 * EAA-1891: one entry in a cross-tree move's unit-type migration plan.
 *
 * `OrgUnitType` is owned by the ROOT org, so moving a subtree into another tree
 * has to bring its vocabulary along or every moved row ends up pointing at a
 * vocabulary belonging to its old root. The server reports what it will do to
 * each type the moved subtree actually USES — not the whole source vocabulary,
 * because this endpoint also moves non-root orgs and re-homing everything would
 * drag types belonging to orgs that are not moving.
 *
 * The three actions are genuinely different outcomes for orgs OUTSIDE the moved
 * subtree, which is why the dialog labels them separately rather than saying
 * "the types will be merged":
 *
 *   - `merge_into_existing` — the destination already has this `code`; moved orgs
 *     are repointed at its row and the source row is retired (`is_active=false`,
 *     never deleted).
 *   - `reown` — no clash and nothing outside the subtree uses it, so the row
 *     itself changes owner. Ids are stable, so nothing is repointed.
 *   - `clone` — no clash, but orgs outside the subtree still use it, so the
 *     destination gets a COPY and only the moved orgs are repointed. Those other
 *     orgs keep the type they had.
 */
export type OrgMoveUnitTypeAction = 'merge_into_existing' | 'reown' | 'clone';

export interface OrgMoveUnitTypePlanEntry {
    source_type_id: string;
    code: string;
    name: string;
    action: OrgMoveUnitTypeAction;
    /** The destination row this maps onto; `null` for `clone` (it does not exist yet). */
    dest_type_id: string | null;
    dest_type_name: string;
}

/**
 * EAA-1891: one reason the move would be refused. The server grades every rule
 * and returns them ALL rather than stopping at the first, because a dialog should
 * show everything the admin has to fix — `validate_parent_change` stops at the
 * first failure, which is right for a write and wrong for a preview.
 *
 * `message` is ENGLISH, unlocalized, exactly like the `details.parent` strings
 * `PATCH parent` returns — translated through `dragRules.translateParentRuleMessage`
 * (`rule` is the stable key; prefer it over matching on the message).
 */
export interface OrgMoveBlocker {
    rule: 'cycle' | 'inactive_parent' | 'unit_type_containment' | 'unknown_unit_type';
    message: string;
}

export interface OrgMoveRef {
    id: string;
    code: string;
    name: string;
    is_active: boolean;
}

export interface OrgMovePreview {
    organization: OrgMoveRef;
    target_parent: OrgMoveRef;
    current_root: OrgMoveRef;
    target_root: OrgMoveRef;
    is_cross_root: boolean;
    is_currently_root: boolean;
    /** Sub-units carried along — the subtree MINUS the org itself. */
    descendants: number;
    /** Distinct accounts across the whole moved subtree, de-duplicated. */
    accounts_affected: number;
    unit_types_merged: OrgMoveUnitTypePlanEntry[];
    blockers: OrgMoveBlocker[];
}

/**
 * EAA-1891: `GET /organizations/{id}/move-preview` — what a cross-tree adoption
 * WOULD do. Writes nothing, so the dialog may call it on every candidate the
 * admin highlights.
 *
 * A missing/unknown `parentId` answers VALIDATION_ERROR, not 404: the org in the
 * path is real, so the failure is about the payload.
 */
export function fetchOrgMovePreview(
    apiBase: string,
    token: string,
    orgId: string,
    params: { parentId: string; unitTypeId?: string }
): Promise<OrgMovePreview> {
    const query = new URLSearchParams({ parent_id: params.parentId });
    if (params.unitTypeId) query.set('unit_type_id', params.unitTypeId);
    return request<OrgMovePreview>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/move-preview?${query.toString()}`
    );
}

/**
 * EAA-1891: `POST /organizations/{id}/move` — adopt this org (and its whole
 * subtree) in under `parent_id`, INCLUDING a parent in a different root tree,
 * migrating the subtree's unit-type vocabulary in the same transaction.
 *
 * DISTINCT FROM `updateOrganization({ parent })` above, and both are kept: that
 * one is the hierarchy screen's drag-and-drop, where the server still refuses a
 * cross-tree target because a drag landing on another tree is almost always a
 * mis-drop. This is the deliberate act, separately permissioned (super-admin
 * only) and with a preview step. Collapsing them into one call would make the
 * drag path silently capable of restructuring two trees at once.
 *
 * `parentId` is required and non-null — "promote to root" remains
 * `updateOrganization({ parent: null })`.
 *
 * `unitTypeId` resolves the one blocker an admin can clear without editing the
 * destination's vocabulary: the moved org's own type not being among the target
 * parent's `allowed_children`. Rule failures come back as VALIDATION_ERROR under
 * `details.parent_id`.
 */
export function moveOrganization(
    apiBase: string,
    token: string,
    orgId: string,
    body: { parent_id: string; unit_type_id?: string }
): Promise<Organization> {
    return request<Organization>(apiBase, token, `/api/v1/organizations/${orgId}/move`, {
        method: 'POST',
        body,
    });
}

/**
 * EAA-1838: the root org's unit-type vocabulary, ordered by name.
 *
 * Ordered by NAME rather than by rank, because there is no rank: a "Sở" may hold
 * both a "Trường" and a "Phòng chuyên môn" with no meaningful order between the
 * two. The shape lives in each row's `allowed_children` and is read as a graph.
 *
 * `activeOnly` is deliberately OPT-IN: the server defaults to returning hidden
 * rows too, and any management surface needs them — a retired type that vanished
 * from the list could never be reactivated. Pass `true` only where an
 * active-only picker is genuinely wanted.
 */
export function fetchOrgUnitTypes(
    apiBase: string,
    token: string,
    rootOrgId: string,
    activeOnly?: boolean
): Promise<OrgUnitType[]> {
    const suffix = activeOnly === undefined ? '' : `?is_active=${String(activeOnly)}`;
    return request<OrgUnitType[]>(
        apiBase,
        token,
        `/api/v1/organizations/${rootOrgId}/unit-types${suffix}`
    );
}

/**
 * EAA-1838: POST a new unit type (super-admin only server-side). `code` is set
 * once, here. `allowed_children` ids outside this root's vocabulary are dropped
 * server-side rather than stored as unreachable edges.
 */
export function createOrgUnitType(
    apiBase: string,
    token: string,
    rootOrgId: string,
    body: { name: string; code: string; allowed_children?: string[]; can_be_root?: boolean }
): Promise<OrgUnitType> {
    return request<OrgUnitType>(apiBase, token, `/api/v1/organizations/${rootOrgId}/unit-types`, {
        method: 'POST',
        body,
    });
}

/**
 * EAA-1838: PATCH one unit type (super-admin only server-side).
 *
 * `code` is absent BY DESIGN — `OrgUnitTypeUpdateSerializer` does not accept it,
 * since it is the stable key external references key off. There is no DELETE:
 * `is_active: false` is the hide path.
 *
 * `allowed_children` REPLACES rather than merges, so an empty array is a
 * meaningful instruction ("this type contains nothing") and must stay
 * distinguishable from omitting the key.
 */
export function updateOrgUnitType(
    apiBase: string,
    token: string,
    rootOrgId: string,
    typeId: string,
    body: {
        name?: string;
        allowed_children?: string[];
        can_be_root?: boolean;
        is_active?: boolean;
    }
): Promise<OrgUnitType> {
    return request<OrgUnitType>(
        apiBase,
        token,
        `/api/v1/organizations/${rootOrgId}/unit-types/${typeId}`,
        { method: 'PATCH', body }
    );
}

/**
 * EAA-1818: deactivate an organization (super-admin only). NEVER a hard delete
 * server-side — Level/OrganizationAdmin/Assignment rows FK the org, so the row
 * stays and only `is_active` flips. The response reports how many distinct
 * accounts the org touches so the confirm dialog can state a real figure
 * instead of the mock's placeholder.
 */
export function deleteOrganization(
    apiBase: string,
    token: string,
    orgId: string
): Promise<OrganizationDeactivated> {
    return request<OrganizationDeactivated>(apiBase, token, `/api/v1/organizations/${orgId}`, {
        method: 'DELETE',
    });
}

export function fetchOrgAdmins(
    apiBase: string,
    token: string,
    orgId: string
): Promise<OrgAdminRow[]> {
    return request<OrgAdminRow[]>(apiBase, token, `/api/v1/organizations/${orgId}/admins`);
}

export function issueOrgAdmin(
    apiBase: string,
    token: string,
    orgId: string,
    body: { email: string; username: string; first_name?: string; last_name?: string }
): Promise<OrgAdminIssued> {
    return request<OrgAdminIssued>(apiBase, token, `/api/v1/organizations/${orgId}/admins`, {
        method: 'POST',
        body,
    });
}

export function fetchOrgTenants(
    apiBase: string,
    token: string,
    orgId: string
): Promise<OrgTenant[]> {
    return request<OrgTenant[]>(apiBase, token, `/api/v1/organizations/${orgId}/tenants`);
}

export function createOrgTenant(
    apiBase: string,
    token: string,
    orgId: string,
    body: { name: string; slug: string; admin_email?: string }
): Promise<OrgTenantCreated> {
    return request<OrgTenantCreated>(apiBase, token, `/api/v1/organizations/${orgId}/tenants`, {
        method: 'POST',
        body,
    });
}

export function updateOrgTenant(
    apiBase: string,
    token: string,
    orgId: string,
    tenantId: string,
    body: { name?: string; is_active?: boolean }
): Promise<OrgTenant> {
    return request<OrgTenant>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/tenants/${tenantId}`,
        {
            method: 'PATCH',
            body,
        }
    );
}

// EAA-1798 (P4c): read shapes for the org's authored grant model (P3c, EAA-1794).
// A Level is the org-scoped assignable position; an Assignment is (user, org,
// level) with the provisioning sync state so drift is visible in the console.
export interface OrgLevel {
    id: string;
    organization_id: string;
    name: string;
    role_key: string;
    description: string;
    template_key: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface OrgAssignment {
    id: string;
    user_sub: string;
    user_email: string;
    organization_id: string;
    level_id: string | null;
    level_name: string | null;
    role_key: string | null;
    is_active: boolean;
    granted_by: string;
    granted_at: string;
    revoked_at: string | null;
    sync_status: 'pending' | 'synced' | 'error';
    last_synced_at: string | null;
    last_sync_error: string;
}

export function fetchOrgLevels(
    apiBase: string,
    token: string,
    orgId: string
): Promise<Paginated<OrgLevel>> {
    return request<Paginated<OrgLevel>>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/levels?page_size=100`
    );
}

// EAA-1807: widened additively with an optional params object so the console
// can page through and filter assignments server-side (the endpoint already
// supported `page`, `page_size`, `is_active`, `ordering` — the client just
// never sent them, always pulling up to 100 rows unfiltered). The 3-arg call
// shape still works for any other caller.
export function fetchOrgAssignments(
    apiBase: string,
    token: string,
    orgId: string,
    params?: { page?: number; page_size?: number; is_active?: boolean }
): Promise<Paginated<OrgAssignment>> {
    const query = new URLSearchParams();
    query.set('page_size', String(params?.page_size ?? 20));
    if (params?.page && params.page > 1) {
        query.set('page', String(params.page));
    }
    if (params?.is_active !== undefined) {
        query.set('is_active', String(params.is_active));
    }
    return request<Paginated<OrgAssignment>>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/assignments?${query.toString()}`
    );
}

// EAA-1799 (P4d): authoring writes over the P3c endpoints (EAA-1794). Deletes are
// soft server-side — a Level DELETE deactivates it, an Assignment DELETE revokes
// it (row kept, revoke pushed) — and both return the updated row.
export function createOrgLevel(
    apiBase: string,
    token: string,
    orgId: string,
    body: { name: string; role_key: string; description?: string; template_key?: string }
): Promise<OrgLevel> {
    return request<OrgLevel>(apiBase, token, `/api/v1/organizations/${orgId}/levels`, {
        method: 'POST',
        body,
    });
}

export function deactivateOrgLevel(
    apiBase: string,
    token: string,
    orgId: string,
    levelId: string
): Promise<OrgLevel> {
    return request<OrgLevel>(apiBase, token, `/api/v1/organizations/${orgId}/levels/${levelId}`, {
        method: 'DELETE',
    });
}

export function createOrgAssignment(
    apiBase: string,
    token: string,
    orgId: string,
    body: { user_sub: string; user_email?: string; level_id: string }
): Promise<OrgAssignment> {
    return request<OrgAssignment>(apiBase, token, `/api/v1/organizations/${orgId}/assignments`, {
        method: 'POST',
        body,
    });
}

export function revokeOrgAssignment(
    apiBase: string,
    token: string,
    orgId: string,
    assignmentId: string
): Promise<OrgAssignment> {
    return request<OrgAssignment>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/assignments/${assignmentId}`,
        { method: 'DELETE' }
    );
}

// EAA-1820 (frame 85, "Chuyển đơn vị"): POST .../assignments/{id}/transfer — moves
// ONE assignment to another organization, in place (the row keeps its id; this is
// not a revoke-then-recreate). Body shape is `AssignmentTransferSerializer`
// (portal-api `org_serializers.py`): `target_organization_id` always required,
// `target_level_id` conditionally so per `AssignmentService.transfer_to_organization`
// (`services.py` ~L2238-2316) — a Level belongs to exactly one organization, so:
//   - level-based assignment (`level_id` set) → `target_level_id` REQUIRED, or the
//     server 400s with "target_level_id is required to move a level-based
//     assignment…".
//   - direct-grant assignment (`level_id` null) → `target_level_id` must be
//     OMITTED, or the server 400s with "target_level_id must not be set for a
//     direct-grant assignment.".
// Every other failure (same org, level not found/wrong org/inactive, duplicate at
// destination, target org not visible to the caller) also comes back as
// `VALIDATION_ERROR` with `details.target_organization_id` — surfaced by the
// caller via `OrgAdminApiError.message`, never swallowed.
export function transferOrgAssignment(
    apiBase: string,
    token: string,
    orgId: string,
    assignmentId: string,
    body: { target_organization_id: string; target_level_id?: string }
): Promise<OrgAssignment> {
    return request<OrgAssignment>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/assignments/${assignmentId}/transfer`,
        { method: 'POST', body }
    );
}

// EAA-1802 (P5b): the org's provisioning coverage (P5a report, EAA-1801) — makes
// drift visible on the console. `status` is 'attention' on a sync error or an
// orphan level; 'pending' is normal while the worker is dark.
export interface ProvisioningStatus {
    assignments: {
        active: number;
        synced: number;
        pending: number;
        error: number;
        revoked: number;
    };
    owned_scopes: number;
    active_levels: number;
    orphan_levels: number;
    status: 'ok' | 'attention';
}

export function fetchProvisioningStatus(
    apiBase: string,
    token: string,
    orgId: string
): Promise<ProvisioningStatus> {
    return request<ProvisioningStatus>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/provisioning-status`
    );
}

/* ===========================================================================
   EAA-1839: org-scoped account listing + bulk Level-assign.

   Backs `/console/hierarchy/accounts` (frames 34/35). Three endpoints, all
   super-admin (any org) / org-admin (own org) server-side:
     GET  /organizations/{id}/accounts
     POST /organizations/{id}/assignments/bulk-preview   (dry-run, writes nothing)
     POST /organizations/{id}/assignments/bulk-apply     (real writes)
   Types below are written against the ACTUAL portal-api shapes
   (`directory/authoring_views.py`, `org_serializers.py`,
   `services.AssignmentService.bulk_preview`/`bulk_apply`), not the mock's
   field names — where the two disagree the mock loses.
   =========================================================================== */

/**
 * One row of `GET /organizations/{id}/accounts`.
 *
 * Structurally identical to `OrgAssignment` BY CONTRACT, not by coincidence:
 * `OrganizationAccountsView` serialises with the very same `AssignmentSerializer`
 * as `OrganizationAssignmentsView` ("reused rather than declaring a second,
 * structurally-identical response shape" — that view's own docstring). Aliased
 * instead of re-declared so the two can never drift apart.
 *
 * WHAT THIS ROW DOES **NOT** CARRY, and therefore what the accounts table cannot
 * show (each one is a real gap, degraded honestly in the UI — never faked):
 *   - No display name and no username. `Assignment` stores `user_sub` +
 *     `user_email` only; there is no Keycloak join on this endpoint. Frame 34's
 *     "Nguyễn Thu Hà / gv.hoa / ha.nguyen@…" three-line identity cell therefore
 *     renders as email + sub.
 *   - No last-login instant, in any form. Confirmed by
 *     `AssignmentService.scoped_accounts_queryset`'s own docstring: Keycloak's
 *     brief user list exposes `createdTimestamp` only, the Admin Events API is
 *     not wired up anywhere in portal-api, and neither `Assignment` nor
 *     `Organization` carries a local login timestamp. Frame 34's "Đăng nhập"
 *     column ("2 giờ", "hôm qua") has no data source at all.
 *   - No organization NAME — only `organization_id`. The "Đơn vị" column resolves
 *     ids through the org rows the screen already fetches.
 */
export type ScopedAccountRow = OrgAssignment;

/**
 * The two issue facets `scoped_accounts_queryset` really implements.
 *
 * There is deliberately NO `never_logged_in` member: that value is NOT supported
 * server-side and is not silently ignored either — it falls through to the
 * "unknown issue facet" branch, which the view turns into a
 * `VALIDATION_ERROR` on `details.issue`. Frame 34's third "VẤN ĐỀ" row ("Chưa
 * đăng nhập lần nào") is rendered disabled with that reason rather than wired to
 * a request guaranteed to 400.
 */
export type ScopedAccountIssue = 'no_level' | 'stale_role_map';

export interface ScopedAccountsParams {
    /** Widen the scope to every descendant organization. Server default: false. */
    includeChildren?: boolean;
    issue?: ScopedAccountIssue;
    levelId?: string;
    /**
     * Drill down to ONE organization inside the current scope. Server semantics
     * (verified in `scoped_accounts_queryset`): the id must already be in the
     * effective scope or the queryset returns nothing at all, and it REPLACES
     * the scope rather than widening it — so a direct child narrows to that child
     * alone, NOT to that child plus its own descendants.
     */
    childOrganizationId?: string;
    isActive?: boolean;
    page?: number;
    /** Server caps this at 100 (`emg_shared.api.common.paginate_queryset`). */
    pageSize?: number;
    /** One of `granted_at` / `user_sub` / `sync_status`, optionally `-` prefixed. */
    ordering?: string;
}

/**
 * `GET /organizations/{orgId}/accounts` — one Assignment row per account grant.
 *
 * NOTE there is no `search` parameter, and its absence is a contract fact rather
 * than an omission here: `OrganizationAccountsView.get` reads exactly
 * `include_children` / `issue` / `level_id` / `child_organization_id` /
 * `is_active` / `ordering` plus the standard `page`/`page_size`. Frame 34's
 * "Tên, username hoặc email" search box has nothing to send — see the screen for
 * how that is surfaced.
 */
export function fetchScopedAccounts(
    apiBase: string,
    token: string,
    orgId: string,
    params: ScopedAccountsParams = {}
): Promise<Paginated<ScopedAccountRow>> {
    const query = new URLSearchParams();
    query.set('page_size', String(params.pageSize ?? 20));
    if (params.page && params.page > 1) query.set('page', String(params.page));
    if (params.includeChildren) query.set('include_children', 'true');
    if (params.issue) query.set('issue', params.issue);
    if (params.levelId) query.set('level_id', params.levelId);
    if (params.childOrganizationId) {
        query.set('child_organization_id', params.childOrganizationId);
    }
    if (params.isActive !== undefined) query.set('is_active', String(params.isActive));
    if (params.ordering) query.set('ordering', params.ordering);
    return request<Paginated<ScopedAccountRow>>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/accounts?${query.toString()}`
    );
}

/** One entry of `BulkLevelPreview.changed` — never a bare sub, unlike the other two buckets. */
export interface BulkLevelChangedEntry {
    user_sub: string;
    assignment_id: string;
    /** `null` on the audited direct-grant path (an active grant with no level). */
    current_level_id: string | null;
}

/**
 * Per-application outcome of the target level, from
 * `AssignmentService._impact_preview` — which resolves through
 * `RoleMapService.resolve_for_provisioning`, the exact primitive the real
 * provisioning path uses.
 *
 * Carries `application_code`, NOT the application's display name: fetching names
 * would mean `GET /admin/apps`, which is `IsSuperAdmin` server-side and would 403
 * for the org-admins this screen is for. Frame 35's "Content Studio / LMS / SIS"
 * labels are therefore shown as the code chip alone.
 */
export interface BulkLevelImpactRow {
    application_id: string;
    application_code: string;
    app_role_keys: string[];
    /** True when the mapping is paused — the level grants NOTHING in this app. */
    paused: boolean;
    reason: string | null;
}

/**
 * `POST .../assignments/bulk-preview` response — the three-way split.
 *
 * `valid_level: false` means `level_id` does not belong to this org (or is
 * inactive); every requested sub then comes back in `skipped` and `impact` is
 * empty, rather than the endpoint raising. Treat it as "nothing can be applied".
 */
export interface BulkLevelPreview {
    valid_level: boolean;
    level_id: string;
    /** Subs with no active grant in this org at all — a plain create. */
    new: string[];
    /** Subs holding a DIFFERENT level — applying revokes the old grant first. */
    changed: BulkLevelChangedEntry[];
    /** Subs already holding this exact level (a no-op), or every sub when the level is invalid. */
    skipped: string[];
    impact: BulkLevelImpactRow[];
}

/** `POST .../assignments/bulk-apply` response. Both arrays hold `user_sub` values. */
export interface BulkLevelApplyResult {
    applied: string[];
    skipped: string[];
}

/** Shared request body of both bulk endpoints (`AssignmentBulkLevelSerializer`). */
export interface BulkLevelAssignInput {
    /** Must be non-empty — the serializer sets `allow_empty=False`, so `[]` is a 400. */
    userSubs: string[];
    levelId: string;
}

/** Dry run. Writes nothing; safe to call on every level change in the dialog. */
export function previewBulkLevelAssign(
    apiBase: string,
    token: string,
    orgId: string,
    input: BulkLevelAssignInput
): Promise<BulkLevelPreview> {
    return request<BulkLevelPreview>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/assignments/bulk-preview`,
        { method: 'POST', body: { user_subs: input.userSubs, level_id: input.levelId } }
    );
}

/**
 * The real writes. `user_subs` is a FLAT list and `bulk_apply` re-derives the
 * buckets itself — there is no "skip the changed ones" flag in the body, so a
 * caller that wants to leave the `changed` bucket alone MUST send a narrowed
 * list. See `useBulkLevelAssign.ts` for that filtering.
 */
export function applyBulkLevelAssign(
    apiBase: string,
    token: string,
    orgId: string,
    input: BulkLevelAssignInput
): Promise<BulkLevelApplyResult> {
    return request<BulkLevelApplyResult>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/assignments/bulk-apply`,
        { method: 'POST', body: { user_subs: input.userSubs, level_id: input.levelId } }
    );
}

/* -------------------------------------------------------------------------- */
/* EAA-1842: console permission catalog                                        */
/* -------------------------------------------------------------------------- */

/** One permission key in the console catalog. */
export interface PermissionEntry {
    /** Wire code, e.g. `console.orgs.manage` — what `useCan()` matches on. */
    code: string;
    /** Enum member name, e.g. `ORGS_MANAGE`. */
    name: string;
    /**
     * "Việc được làm" — plain-language Vietnamese. Served by the API rather than
     * held in a frontend lookup ON PURPOSE: a code and its description have to be
     * added together, or the next backend-only permission renders here as a bare
     * dotted string, which is the exact problem this screen exists to fix.
     */
    label: string;
    /**
     * Language-NEUTRAL group key (`orgs`, `accounts`, …). Filter on this, never on
     * `group_label` — the label follows Accept-Language, so filtering by it would
     * break the moment the user switches language.
     */
    group: string;
    /** Translated group name for display. Normalised client-side if absent. */
    group_label: string;
    /** True when the action writes into another system — the "ghi ra ngoài" chip. */
    writes_externally: boolean;
    /**
     * Gates no endpoint at all — it only shows/hides a nav item, so its
     * `endpoint_count` is legitimately 0. Without the chip that zero reads as a
     * bug. Optional so an older server (which never sent it) still type-checks.
     */
    menu_only?: boolean;
    /** How many routed endpoints require this code, measured from the live URL conf. */
    endpoint_count: number;
}

/** A built-in console role and the codes it grants. */
export interface RolePermissions {
    key: string;
    label: string;
    permissions: string[];
}

/**
 * `GET /api/v1/permissions` — the catalog, the role map, and the caller's own set.
 *
 * Served from the SAME structures the server enforces against, so this screen
 * shows the live rule set rather than a documentation copy that can rot. Read
 * only: portal has no console-role model yet, so there is nothing an editor
 * could legitimately change.
 */
export interface PermissionGroup {
    /** Stable key used for filtering. */
    key: string;
    /** Translated display name. */
    label: string;
}

export interface PermissionCatalog {
    /** Language the server resolved from Accept-Language. */
    lang: string;
    /** Filter-chip order, server-owned so the UI cannot invent a bucket. */
    groups: PermissionGroup[];
    permissions: PermissionEntry[];
    roles: RolePermissions[];
    mine: { roles: string[]; permissions: string[] };
}

/**
 * Raw wire shape, before normalisation. `groups` was `string[]` before EAA-1843
 * added i18n and is `{key,label}[]` after.
 */
interface RawPermissionCatalog extends Omit<PermissionCatalog, 'groups' | 'lang'> {
    lang?: string;
    groups: (string | PermissionGroup)[];
}

/**
 * EAA-1843: normalise the catalog so BOTH wire shapes render.
 *
 * This is not defensive noise — it is the fix for a real break. Making `groups`
 * an object array was a breaking change to a payload an already-loaded client
 * was still consuming, so every open tab crashed with "Objects are not valid as
 * a React child" until it was reloaded. In dev that is a stale HMR bundle; in
 * production it is every user who had the app open during a deploy.
 *
 * Normalising at the boundary means an old client tolerates the new payload
 * (it just shows the key instead of a translated label) and a new client
 * tolerates an old server during a rolling deploy. Components downstream see one
 * shape and never branch on it.
 */
function normalizeCatalog(raw: RawPermissionCatalog): PermissionCatalog {
    const groups: PermissionGroup[] = (raw.groups ?? []).map((g) =>
        typeof g === 'string' ? { key: g, label: g } : g
    );
    const labelByKey = new Map(groups.map((g) => [g.key, g.label]));
    return {
        ...raw,
        lang: raw.lang ?? 'vi',
        groups,
        permissions: (raw.permissions ?? []).map((p) => ({
            ...p,
            // Older servers send no `group_label`; fall back to the group's own
            // label, then to the raw key, so a cell is never blank.
            group_label: p.group_label ?? labelByKey.get(p.group) ?? p.group,
        })),
    };
}

export async function fetchPermissionCatalog(
    apiBase: string,
    token: string
): Promise<PermissionCatalog> {
    const raw = await request<RawPermissionCatalog>(apiBase, token, '/api/v1/permissions');
    return normalizeCatalog(raw);
}

/* -------------------------------------------------------------------------- */
/* EAA-1843: custom console roles, assignments, user groups                    */
/* -------------------------------------------------------------------------- */

export interface ConsoleRole {
    /** `null` for the two built-ins — they are source-defined, not rows. */
    id: string | null;
    code: string;
    name: string;
    description: string;
    /** Catalog permission codes this role grants. */
    permissions: string[];
    is_active: boolean;
    /** Built-ins are read-only: the API refuses to edit them. */
    is_builtin: boolean;
    assignment_count?: number;
}

export interface ConsoleRoleList {
    builtin: ConsoleRole[];
    custom: ConsoleRole[];
}

export interface ConsoleRoleAssignment {
    id: string;
    role: string;
    user_sub: string;
    group: string | null;
    group_name: string | null;
    organization: string | null;
    is_active: boolean;
    granted_by: string;
    created_at: string;
}

/** A role a group currently holds — `organization: null` means system-wide. */
export interface HeldRole {
    id: string;
    code: string;
    name: string;
    organization: string | null;
}

export interface ConsoleUserGroup {
    id: string;
    organization: string;
    name: string;
    description: string;
    is_active: boolean;
    member_count: number;
    members: { user_sub: string; user_email: string }[];
    /** Roles granted to this group; revoked/deactivated ones are excluded. */
    roles: HeldRole[];
    updated_at: string;
}

export function fetchConsoleRoles(apiBase: string, token: string): Promise<ConsoleRoleList> {
    return request<ConsoleRoleList>(apiBase, token, '/api/v1/console-roles');
}

export function createConsoleRole(
    apiBase: string,
    token: string,
    body: { code: string; name: string; description?: string; permissions?: string[] }
): Promise<ConsoleRole> {
    return request<ConsoleRole>(apiBase, token, '/api/v1/console-roles', { method: 'POST', body });
}

/**
 * `code` is absent by design — it is the stable identifier, set once at create.
 * `permissions` REPLACES the set when present, so an empty array is a real
 * instruction ("this role grants nothing").
 */
export function updateConsoleRole(
    apiBase: string,
    token: string,
    roleId: string,
    body: { name?: string; description?: string; permissions?: string[]; is_active?: boolean }
): Promise<ConsoleRole> {
    return request<ConsoleRole>(apiBase, token, `/api/v1/console-roles/${roleId}`, {
        method: 'PATCH',
        body,
    });
}

export function fetchRoleAssignments(
    apiBase: string,
    token: string,
    roleId: string
): Promise<ConsoleRoleAssignment[]> {
    return request<ConsoleRoleAssignment[]>(
        apiBase,
        token,
        `/api/v1/console-roles/${roleId}/assignments`
    );
}

/** Exactly one of `user_sub` / `group`; omit `organization` for system-wide. */
export function assignConsoleRole(
    apiBase: string,
    token: string,
    roleId: string,
    body: { user_sub?: string; group?: string; organization?: string | null }
): Promise<ConsoleRoleAssignment> {
    return request<ConsoleRoleAssignment>(
        apiBase,
        token,
        `/api/v1/console-roles/${roleId}/assignments`,
        { method: 'POST', body }
    );
}

export function revokeConsoleRoleAssignment(
    apiBase: string,
    token: string,
    roleId: string,
    assignmentId: string
): Promise<void> {
    return request<void>(
        apiBase,
        token,
        `/api/v1/console-roles/${roleId}/assignments/${assignmentId}`,
        { method: 'DELETE' }
    );
}

export function fetchUserGroups(
    apiBase: string,
    token: string,
    orgId: string
): Promise<ConsoleUserGroup[]> {
    return request<ConsoleUserGroup[]>(apiBase, token, `/api/v1/organizations/${orgId}/user-groups`);
}

export function createUserGroup(
    apiBase: string,
    token: string,
    orgId: string,
    body: { name: string; description?: string; members?: { user_sub: string; user_email?: string }[] }
): Promise<ConsoleUserGroup> {
    return request<ConsoleUserGroup>(apiBase, token, `/api/v1/organizations/${orgId}/user-groups`, {
        method: 'POST',
        body,
    });
}

/** `members` REPLACES the roster when present — empty array means nobody. */
export function updateUserGroup(
    apiBase: string,
    token: string,
    orgId: string,
    groupId: string,
    body: {
        name?: string;
        description?: string;
        is_active?: boolean;
        members?: { user_sub: string; user_email?: string }[];
    }
): Promise<ConsoleUserGroup> {
    return request<ConsoleUserGroup>(
        apiBase,
        token,
        `/api/v1/organizations/${orgId}/user-groups/${groupId}`,
        { method: 'PATCH', body }
    );
}
